import { Link } from 'react-router'

export function TermsOfServicePage() {
  return (
    <div className="bg-background text-foreground min-h-screen">
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <header className="mb-8">
          <Link to="/" className="text-primary hover:text-primary/80 text-sm font-medium">
            &larr; Back to CASSI
          </Link>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">Terms of Service</h1>
          <p className="text-muted-foreground mt-2 text-sm">MAGIG Design + Technologies UG</p>
          <p className="text-muted-foreground text-sm">Last updated: January 19, 2026</p>
        </header>

        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <p className="text-muted-foreground lead">
            Welcome to CASSI! By accessing or using CASSI (the "Service"), you agree to the
            following Terms and Conditions. Please read them carefully.
          </p>

          <hr className="border-border my-8" />

          <Section title="1. Who We Are">
            <p>
              CASSI is developed and operated by MAGIG Design + Technologies UG
              (haftungsbeschränkt), a company registered in Hamburg, Germany. We provide an
              AI-powered assistant that helps you create social media content quickly, simply, and
              in your unique voice.
            </p>
            <address className="not-italic">
              MAGIG Design + Technologies UG
              <br />
              Hochallee 19
              <br />
              20149 Hamburg
              <br />
              Germany
              <br />
              VAT ID: DE343829768
            </address>
            <p>
              If you have any questions, you can contact us at:{' '}
              <a href="mailto:ingo@magig.de" className="text-primary hover:underline">
                ingo@magig.de
              </a>
            </p>
          </Section>

          <Section title="2. Acceptance of Terms">
            <p>
              By accessing or using CASSI, you agree to be bound by these Terms of Service. If you
              do not agree, please do not use the Service.
            </p>
            <p>
              <strong>Eligibility:</strong> You must be at least 18 years old to use CASSI. By using
              the Service, you represent and warrant that you meet this age requirement and have the
              legal capacity to enter into these Terms.
            </p>
            <p>
              If you are using CASSI on behalf of an organization, you represent and warrant that
              you have the authority to bind that organization to these Terms.
            </p>
          </Section>

          <Section title="3. Description of Service">
            <p>
              CASSI is an AI-powered social media content creation platform that enables you to:
            </p>
            <ul>
              <li>Create and edit social media posts using AI assistance</li>
              <li>Connect your social media accounts (Instagram, LinkedIn, TikTok, YouTube)</li>
              <li>Schedule and publish content to connected platforms</li>
              <li>Manage multiple brand moodboards and content styles</li>
              <li>Generate images using AI technology</li>
              <li>Import and analyze existing social media posts for inspiration</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any part of the Service at any
              time, with or without notice. We will make reasonable efforts to notify users of
              significant changes that may affect their use of the Service.
            </p>
          </Section>

          <Section title="4. Your Use of the Service">
            <p>
              We grant you a non-exclusive, non-transferable, revocable license to use CASSI in
              accordance with these Terms.
            </p>
            <h4 className="text-foreground font-semibold">You agree not to:</h4>
            <ul>
              <li>Use the Service for illegal, harmful, or unethical purposes</li>
              <li>
                Attempt to reverse-engineer, decompile, or replicate the underlying AI systems
              </li>
              <li>
                Use the output in a way that violates intellectual property rights of third parties
              </li>
              <li>Create or spread misinformation, disinformation, or deceptive content</li>
              <li>Promote hate, violence, discrimination, or harassment</li>
              <li>
                Impersonate others or misrepresent your identity or affiliation with any person or
                organization
              </li>
              <li>Circumvent, disable, or interfere with security features of the Service</li>
              <li>
                Use automated systems (bots, scrapers, etc.) to access the Service without our
                express written permission
              </li>
              <li>Share your account credentials or allow others to access your account</li>
              <li>Exceed any rate limits or usage quotas applicable to your subscription plan</li>
            </ul>
          </Section>

          <Section title="5. Account Registration and Security">
            <p>To use certain features of CASSI, you must create an account. You agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Notify us immediately of any unauthorized access to or use of your account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that contain inaccurate
              information or violate these Terms.
            </p>
          </Section>

          <Section title="6. Content Ownership and Rights">
            <h4 className="text-foreground font-semibold">Your Content:</h4>
            <ul>
              <li>
                You retain full ownership of the content you input into CASSI and the outputs you
                generate using the Service
              </li>
              <li>
                We do not claim ownership of your content and do not use your inputs or outputs for
                advertising or marketing without your consent
              </li>
              <li>
                You grant us a limited license to process, store, and display your content solely to
                provide and improve the Service
              </li>
            </ul>

            <h4 className="text-foreground font-semibold">Our Content:</h4>
            <ul>
              <li>
                All intellectual property related to the CASSI platform, including its AI models,
                software, branding, interface, documentation, and trademarks, is owned by MAGIG
                Design + Technologies UG and protected under applicable law
              </li>
              <li>
                You may not copy, modify, distribute, or create derivative works of our proprietary
                content without our express written permission
              </li>
            </ul>

            <h4 className="text-foreground font-semibold">Feedback:</h4>
            <p>
              If you provide us with feedback, suggestions, or ideas about the Service, you grant us
              a perpetual, worldwide, royalty-free license to use, modify, and incorporate such
              feedback into the Service without any obligation to you.
            </p>
          </Section>

          <Section title="7. AI-Generated Content Disclaimer">
            <p>
              CASSI uses artificial intelligence to generate content. While we aim for accuracy,
              creativity, and quality, please be aware of the following:
            </p>
            <ul>
              <li>
                <strong>No guarantee of accuracy:</strong> AI-generated content may contain errors,
                inaccuracies, or outdated information. We cannot guarantee that all outputs will be
                factually correct or suitable for every purpose.
              </li>
              <li>
                <strong>Your responsibility:</strong> You are solely responsible for reviewing,
                verifying, and approving all content before publishing. This includes checking for
                factual accuracy, appropriateness, and compliance with applicable laws and platform
                policies.
              </li>
              <li>
                <strong>Not professional advice:</strong> AI-generated content does not constitute
                legal, financial, medical, or other professional advice. Consult qualified
                professionals for such matters.
              </li>
              <li>
                <strong>Platform compliance:</strong> You are responsible for ensuring that any
                content you publish complies with the terms of service and community guidelines of
                the social media platforms where you publish.
              </li>
            </ul>
          </Section>

          <Section title="8. Subscriptions and Payments">
            <h4 className="text-foreground font-semibold">Free and Paid Plans:</h4>
            <p>
              CASSI offers both free and paid subscription plans. Features, usage limits, and
              pricing are described on our website and may be updated from time to time.
            </p>

            <h4 className="text-foreground font-semibold">Billing:</h4>
            <ul>
              <li>
                Paid subscriptions are billed in advance on a monthly or annual basis, depending on
                the plan you select
              </li>
              <li>
                All payments are processed securely through Stripe. By subscribing, you also agree
                to Stripe's terms of service
              </li>
              <li>Prices are displayed in EUR and may be subject to applicable taxes</li>
            </ul>

            <h4 className="text-foreground font-semibold">Automatic Renewal:</h4>
            <p>
              Subscriptions automatically renew at the end of each billing period unless you cancel
              before the renewal date. You can cancel your subscription at any time through your
              account settings.
            </p>

            <h4 className="text-foreground font-semibold">Refunds:</h4>
            <p>
              We generally do not provide refunds for subscription fees already paid. However, if
              you believe you are entitled to a refund due to exceptional circumstances, please
              contact us at{' '}
              <a href="mailto:ingo@magig.de" className="text-primary hover:underline">
                ingo@magig.de
              </a>
              , and we will consider your request on a case-by-case basis.
            </p>

            <h4 className="text-foreground font-semibold">Price Changes:</h4>
            <p>
              We may change subscription prices from time to time. We will provide you with at least
              30 days' notice before any price increase takes effect. If you do not agree to the new
              pricing, you may cancel your subscription before the change takes effect.
            </p>
          </Section>

          <Section title="9. Third-Party Services and Social Media Platforms">
            <p>
              CASSI integrates with third-party social media platforms (Instagram, LinkedIn, TikTok,
              YouTube) and other services. By connecting these accounts:
            </p>
            <ul>
              <li>
                You authorize CASSI to access and interact with your accounts on these platforms as
                necessary to provide the Service
              </li>
              <li>
                You agree to comply with the terms of service and policies of each connected
                platform
              </li>
              <li>
                We are not responsible for the availability, accuracy, or policies of third-party
                services
              </li>
              <li>
                You understand that disconnecting a platform will remove our access to that platform
                and may affect your ability to use certain features
              </li>
            </ul>
            <p>
              We are not affiliated with, endorsed by, or sponsored by Meta (Instagram), Microsoft
              (LinkedIn), ByteDance (TikTok), or Google (YouTube). All trademarks are the property
              of their respective owners.
            </p>
          </Section>

          <Section title="10. Data Privacy">
            <p>
              Your privacy is important to us. We do not use your data for ads or third-party
              marketing. Our data practices are described in detail in our{' '}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              , which is incorporated into these Terms by reference.
            </p>
            <p>
              By using CASSI, you acknowledge that you have read and understood our Privacy Policy.
            </p>
          </Section>

          <Section title="11. Service Availability">
            <p>We strive to provide a reliable and continuously available Service. However:</p>
            <ul>
              <li>We do not guarantee uninterrupted or error-free operation of the Service</li>
              <li>
                We may perform scheduled maintenance, which we will attempt to announce in advance
                when possible
              </li>
              <li>
                We are not responsible for interruptions caused by factors beyond our reasonable
                control, including third-party service outages, internet connectivity issues, or
                force majeure events
              </li>
            </ul>
          </Section>

          <Section title="12. Account Suspension and Termination">
            <h4 className="text-foreground font-semibold">By You:</h4>
            <p>
              You may cancel your account at any time by using the account deletion feature in your
              settings or by contacting us. Upon cancellation, your access to the Service will end,
              and your data will be deleted in accordance with our Privacy Policy.
            </p>

            <h4 className="text-foreground font-semibold">By Us:</h4>
            <p>
              We reserve the right to suspend or terminate your access to the Service, with or
              without notice, if:
            </p>
            <ul>
              <li>You violate these Terms of Service</li>
              <li>You engage in fraudulent or illegal activity</li>
              <li>Your use of the Service poses a security risk to us or others</li>
              <li>
                We are required to do so by law or by order of a court or government authority
              </li>
              <li>We discontinue the Service (with reasonable notice)</li>
            </ul>
          </Section>

          <Section title="13. Limitation of Liability">
            <p>To the fullest extent permitted by applicable law:</p>
            <ul>
              <li>
                <strong>Disclaimer of warranties:</strong> The Service is provided "as is" and "as
                available" without warranties of any kind, whether express, implied, or statutory,
                including implied warranties of merchantability, fitness for a particular purpose,
                and non-infringement.
              </li>
              <li>
                <strong>Limitation of damages:</strong> We will not be liable for any indirect,
                incidental, special, consequential, or punitive damages, including loss of profits,
                data, use, or goodwill, arising out of or related to your use of the Service.
              </li>
              <li>
                <strong>Maximum liability:</strong> Our total liability for any claims arising from
                or related to these Terms or the Service shall not exceed the amount you paid us in
                the 12 months preceding the claim.
              </li>
            </ul>
            <p>
              These limitations apply regardless of the theory of liability (contract, tort,
              negligence, strict liability, or otherwise) and even if we have been advised of the
              possibility of such damages.
            </p>
            <p>
              Some jurisdictions do not allow the exclusion of certain warranties or limitations of
              liability, so some of the above limitations may not apply to you. In such cases, our
              liability will be limited to the maximum extent permitted by law.
            </p>
          </Section>

          <Section title="14. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless MAGIG Design + Technologies UG, its
              officers, directors, employees, and agents from and against any claims, liabilities,
              damages, losses, and expenses (including reasonable legal fees) arising out of or
              related to:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of third parties</li>
              <li>Any content you create, publish, or distribute using the Service</li>
            </ul>
          </Section>

          <Section title="15. Modifications to These Terms">
            <p>
              We may update these Terms from time to time to reflect changes in our practices,
              technology, legal requirements, or other factors.
            </p>
            <p>
              If we make material changes, we'll notify you via email or in-app notification at
              least 30 days before the changes take effect.
            </p>
            <p>
              Continued use of CASSI after the updated Terms take effect means you accept the
              updated terms. If you do not agree to the updated Terms, you should stop using the
              Service.
            </p>
          </Section>

          <Section title="16. Governing Law and Dispute Resolution">
            <p>
              These Terms are governed by the laws of the Federal Republic of Germany, without
              regard to its conflict of laws principles.
            </p>
            <p>
              Any disputes arising from or relating to these Terms or the Service shall be subject
              to the exclusive jurisdiction of the courts in Hamburg, Germany.
            </p>
            <p>
              Notwithstanding the above, you acknowledge that we may seek injunctive or other
              equitable relief in any court of competent jurisdiction to protect our intellectual
              property rights.
            </p>
          </Section>

          <Section title="17. General Provisions">
            <h4 className="text-foreground font-semibold">Entire Agreement:</h4>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between
              you and MAGIG Design + Technologies UG regarding the Service and supersede all prior
              agreements and understandings.
            </p>

            <h4 className="text-foreground font-semibold">Severability:</h4>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that
              provision will be limited or eliminated to the minimum extent necessary, and the
              remaining provisions will remain in full force and effect.
            </p>

            <h4 className="text-foreground font-semibold">Waiver:</h4>
            <p>
              Our failure to enforce any right or provision of these Terms will not be considered a
              waiver of that right or provision.
            </p>

            <h4 className="text-foreground font-semibold">Assignment:</h4>
            <p>
              You may not assign or transfer these Terms or your rights under them without our prior
              written consent. We may assign our rights and obligations under these Terms without
              restriction.
            </p>
          </Section>

          <Section title="18. Contact Us">
            <p>If you have any questions about these Terms of Service, please contact us:</p>
            <address className="not-italic">
              MAGIG Design + Technologies UG
              <br />
              Hochallee 19
              <br />
              20149 Hamburg
              <br />
              Germany
              <br />
              Email:{' '}
              <a href="mailto:ingo@magig.de" className="text-primary hover:underline">
                ingo@magig.de
              </a>
            </address>
          </Section>

          <hr className="border-border my-8" />

          <footer className="text-muted-foreground text-sm">
            <p>
              <strong>MAGIG Design + Technologies UG</strong>
            </p>
            <p>Last updated: January 19, 2026</p>
          </footer>
        </div>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="my-8">
      <h2 className="text-foreground mb-4 text-xl font-semibold">{title}</h2>
      <div className="text-muted-foreground space-y-4">{children}</div>
    </section>
  )
}
